/**
 * Addon Store panels — widget-first (2.1).
 * Data: callWidgetTool (node-aware). NodePicker v ModuleScopeBar cílí install
 * na vybraný nod (mesh.rpc_call), ne na lokál.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { StatCard, StatsGrid } from '../../../components/ui/Stats';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { EmptyState } from '../../../components/ui/EmptyState';
import { RefreshButton } from '../../../components/ui/RefreshButton';
import { MaturityBadge } from '../../../components/ui/MaturityBadge';
import {
  Box,
  CheckCircle2,
  Download,
  ExternalLink,
  GitBranch,
  Globe,
  Home,
  Package,
  Plus,
  RotateCw,
  Search,
  Shield,
  Trash2,
  Wrench,
} from 'lucide-react';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { moduleDisplayName } from '../../../lib/module-identity';
import { nestModules } from '../../../lib/module-tree';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';

// ── Types ──────────────────────────────────────────────────────────────────

interface InstalledModule {
  name: string;
  version: string;
  description: string;
  maturity: string;
  core: boolean;
  tools: string[];
  ui: { section: string; icon: string; label: string } | null;
  parent?: string | null;
  store_kind?: 'addon' | 'submodule' | 'internal';
  enabled?: boolean;
}

interface RegistryModule {
  name: string;
  version: string;
  description: string;
  maturity: string;
  author: string;
  source: string;
  tags: string[];
}

interface HaRepo {
  name: string;
  url: string;
  addons?: HaAddon[];
}

interface HaAddon {
  name: string;
  slug: string;
  description: string;
  version: string;
  image?: string;
  url?: string;
  arch?: string[];
  repository?: string;
  ports?: Record<string, number | string>;
  env?: Record<string, unknown>;
  map?: string[];
  fullConfig?: string;
}

interface HacsRepo {
  name: string;
  url: string;
  type: 'github' | 'gitlab';
  addons?: HacsAddon[];
}

interface HacsAddon {
  name: string;
  description: string;
  version: string;
  url: string;
  repository: string;
  hacsData?: unknown;
}

const DEFAULT_HA_REPOS = [
  {
    name: 'Official HA Addons',
    url: 'https://api.github.com/repos/home-assistant/addons/contents?ref=master',
  },
  {
    name: 'Community Addons',
    url: 'https://api.github.com/repos/hassio-addons/repository/contents?ref=master',
  },
];

const DEFAULT_HACS_REPOS: HacsRepo[] = [];

function nodeKey(scope: EffectiveScope): string {
  return scope.node ?? 'local';
}

function callMcp<T = unknown>(
  scope: EffectiveScope,
  name: string,
  args: Record<string, unknown> = {},
  opts?: { silent?: boolean; label?: string },
) {
  return callWidgetTool<T>(name, args, scope, opts);
}

function AvailableRow({
  m,
  picked,
  setPicked,
  installMut,
  t,
  nested,
}: {
  m: {
    name: string;
    version: string;
    description: string;
    maturity: string;
    source: string;
    url?: string;
    parent?: string;
  };
  picked: string[];
  setPicked: (fn: (prev: string[]) => string[]) => void;
  installMut: { mutate: (a: { name: string; url?: string }) => void; isPending: boolean };
  t: (k: string, fb?: string | Record<string, unknown>) => string;
  nested?: boolean;
}) {
  return (
    <Card>
      <CardContent className='py-3'>
        <div className='flex items-center gap-3'>
          {m.source === 'local (disabled)' && (
            <input
              type='checkbox'
              className='accent-sky-500 shrink-0'
              checked={picked.includes(m.name)}
              onChange={(e) =>
                setPicked((prev) =>
                  e.target.checked ? [...prev, m.name] : prev.filter((x) => x !== m.name)
                )}
            />
          )}
          <Package size={16} className={nested ? 'text-sky-500 flex-shrink-0' : 'text-gray-500 flex-shrink-0'} />
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold text-white'>{m.name}</span>
              <span className='text-[10px] text-gray-600 font-mono'>v{m.version}</span>
              <MaturityBadge maturity={m.maturity} showStable />
              <Badge variant='secondary'>
                {m.source === 'local (disabled)' ? t('addonStore.sourceLocalDisabled') : m.source}
              </Badge>
              {nested && m.parent && m.parent !== 'acm' && (
                <Badge variant='secondary'>{t('addonStore.partOf', { parent: m.parent })}</Badge>
              )}
            </div>
            <p className='text-xs text-gray-500 truncate'>{m.description}</p>
          </div>
          <Button
            variant='primary'
            size='sm'
            onClick={() => installMut.mutate({ name: m.name, url: m.url })}
            loading={installMut.isPending}
            icon={<Download size={12} />}
          >
            {m.source === 'local (disabled)' ? t('addonStore.enable') : t('addonStore.install')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function InstalledRow({
  m,
  t,
  toggleMut,
  removeMut,
  nested,
}: {
  m: InstalledModule;
  t: (k: string, fb?: string | Record<string, unknown>) => string;
  toggleMut: { mutate: (a: { name: string; enable: boolean }) => void; isPending: boolean };
  removeMut: { mutate: (name: string) => void };
  nested?: boolean;
}) {
  return (
    <Card>
      <CardContent className='py-3'>
        <div className='flex items-center gap-3'>
          <Box size={16} className={nested ? 'text-sky-500 flex-shrink-0' : 'text-gray-500 flex-shrink-0'} />
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold text-white'>
                {moduleDisplayName(m, (k, fb) => t(k, fb ?? ''))}
              </span>
              <span className='text-[10px] text-gray-600 font-mono'>v{m.version}</span>
              <MaturityBadge maturity={m.maturity} />
              {m.core && <Badge variant='info'>{t('addonStore.coreBadge')}</Badge>}
              {nested && m.parent && (
                <Badge variant='secondary'>{t('addonStore.partOf', { parent: m.parent })}</Badge>
              )}
            </div>
            <p className='text-xs text-gray-500 truncate'>{m.description}</p>
          </div>
          <div className='flex gap-1 flex-shrink-0'>
            <CheckCircle2 size={14} className='text-emerald-400' />
            {!m.core && (
              <>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => toggleMut.mutate({ name: m.name, enable: false })}
                >
                  {t('addonStore.disable')}
                </Button>
                <Button
                  variant='danger'
                  size='sm'
                  onClick={async () => {
                    if (await themedConfirm(t('addonStore.confirmRemoveModule', { name: m.name }))) {
                      removeMut.mutate(m.name);
                    }
                  }}
                  icon={<Trash2 size={12} />}
                />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Installed ──────────────────────────────────────────────────────────────

function InstalledTab({ scope }: { scope: EffectiveScope }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const nk = nodeKey(scope);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['addon-installed', nk],
    queryFn: () =>
      callMcp<{ modules: InstalledModule[] }>(scope, 'toolbox.sysupdate_module', { action: 'list' }),
    staleTime: 30_000,
  });

  const allMods = (data?.modules ?? []).filter((m) => m.enabled !== false);
  const modules = search.trim()
    ? allMods
    : allMods.filter((m) => (m.store_kind ?? 'addon') !== 'internal');

  const toggleMut = useMutation({
    mutationFn: (args: { name: string; enable: boolean }) =>
      callMcp(scope, 'toolbox.sysupdate_module', {
        action: args.enable ? 'enable' : 'disable',
        name: args.name,
        restart: false,
      }),
    onSuccess: () => {
      toast.success(t('addonStore.toastEnableApply'));
      qc.invalidateQueries({ queryKey: ['addon-installed', nk] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeMut = useMutation({
    mutationFn: (name: string) =>
      callMcp(scope, 'toolbox.sysupdate_module', { action: 'remove', module: name }),
    onSuccess: () => {
      toast.success(t('addonStore.toastModuleRemoved'));
      refetch();
    },
  });

  const restartMut = useMutation({
    mutationFn: () => callMcp(scope, 'toolbox.sysupdate_restart', { service: 'api' }),
    onSuccess: () => toast.success(t('addonStore.toastApiRestarted')),
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = modules.filter((m) =>
    !search || m.name.includes(search.toLowerCase()) ||
    (m.description || '').toLowerCase().includes(search.toLowerCase())
  );
  const tree = nestModules(filtered);
  const coreCount = modules.filter((m) => (m as unknown as { core?: boolean }).core).length;

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2 flex-wrap'>
        <ChannelBadge scope={scope} />
        <Button
          variant='secondary'
          size='sm'
          onClick={async () => {
            if (await themedConfirm(t('addonStore.confirmRestartApi'))) {
              restartMut.mutate();
            }
          }}
          loading={restartMut.isPending}
          icon={<RotateCw size={14} />}
          title={t('addonStore.restartApiTitle')}
        >
          {t('addonStore.restartApi')}
        </Button>
      </div>

      <StatsGrid columns={3}>
        <StatCard label={t('addonStore.statInstalled')} value={modules.length} icon={Package} />
        <StatCard label={t('addonStore.statCore')} value={coreCount} icon={Shield} variant='info' />
        <StatCard
          label={t('addonStore.statTools')}
          value={modules.reduce((s, m) => s + (m.tools?.length ?? 0), 0)}
          icon={Wrench}
        />
      </StatsGrid>

      <div className='flex items-center gap-2'>
        <div className='flex-1 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
          <Search size={14} className='text-gray-600' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('addonStore.searchPlaceholder')}
            className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
          />
        </div>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='md' />
      </div>

      <div className='space-y-2'>
        {tree.map((m) => (
          <div key={m.name} className='space-y-1'>
            <InstalledRow
              m={m}
              t={t}
              toggleMut={toggleMut}
              removeMut={removeMut}
            />
            {m.children.map((c) => (
              <div key={c.name} className='pl-6'>
                <InstalledRow m={c} t={t} toggleMut={toggleMut} removeMut={removeMut} nested />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Available ──────────────────────────────────────────────────────────────

function AvailableTab({ scope }: { scope: EffectiveScope }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [packFilter, setPackFilter] = useState<string | null>(null);
  const nk = nodeKey(scope);

  const { data: bundlesData } = useQuery({
    queryKey: ['addon-bundles', nk],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        bundles?: Array<{
          id: string;
          title: string;
          description: string;
          to_enable: string[];
          modules: Array<{ name: string; enabled: boolean; installed: boolean }>;
        }>;
      }>(scope, 'toolbox.sysupdate_module', { action: 'bundles' }),
    staleTime: 15_000,
  });
  const bundles = bundlesData?.bundles ?? [];
  const activePack = bundles.find((b) => b.id === packFilter) ?? null;

  const restartMut = useMutation({
    mutationFn: () => callMcp(scope, 'toolbox.sysupdate_restart', { service: 'api' }),
    onSuccess: () => toast.success(t('addonStore.toastApiRestarted')),
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: envData } = useQuery({
    queryKey: ['addon-server-info', nk],
    queryFn: () => callMcp<{ env: string }>(scope, 'acm-self.ui_context'),
    staleTime: 60_000,
  });
  const serverEnv = envData?.env ?? 'dev';

  const { data: instData } = useQuery({
    queryKey: ['addon-installed', nk],
    queryFn: () => callMcp<{ modules: InstalledModule[] }>(scope, 'acm-self.modules_list'),
    staleTime: 30_000,
  });
  const installed = new Set((instData?.modules ?? []).map((m) => m.name));

  const { data: allData, isLoading, refetch } = useQuery({
    queryKey: ['addon-all-modules', nk],
    queryFn: () =>
      callMcp<{ modules?: InstalledModule[] }>(scope, 'toolbox.sysupdate_module', { action: 'list' }),
    staleTime: 30_000,
  });

  const { data: regData } = useQuery({
    queryKey: ['addon-registry', nk],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        entries?: RegistryModule[];
        results?: RegistryModule[];
        modules?: RegistryModule[];
      }>(scope, 'registry.catalog', { action: 'list' }).catch(() => ({
        ok: false,
        entries: [] as RegistryModule[],
        results: [] as RegistryModule[],
        modules: [] as RegistryModule[],
      })),
    staleTime: 60_000,
  });

  const localModules = (allData?.modules ?? []) as Array<{
    name: string;
    version: string;
    description: string;
    maturity: string;
    enabled: boolean;
    core: boolean;
    has_ui: boolean;
    parent?: string | null;
    store_kind?: 'addon' | 'submodule' | 'internal';
  }>;
  const registryModules = regData?.modules ?? regData?.entries ?? regData?.results ?? [];

  const allowedMaturity = serverEnv === 'production'
    ? ['production', 'stable']
    : serverEnv === 'staging'
    ? ['production', 'stable', 'beta', 'testing']
    : null;
  const matchesMaturity = (m: { maturity?: string }) =>
    !allowedMaturity || allowedMaturity.includes(m.maturity ?? 'stable');
  const storeVisible = (m: { store_kind?: string; name?: string }) =>
    search.trim() ? true : (m.store_kind ?? 'addon') !== 'internal';
  const disabledLocal = localModules.filter((m) => !m.enabled).filter(matchesMaturity).filter(storeVisible);
  const remoteOnly = registryModules
    .filter((m) => !localModules.some((l) => l.name === m.name))
    .filter((m) => !installed.has(m.name))
    .filter(matchesMaturity)
    .filter((m) => !(m.tags ?? []).includes('core'));

  const installMut = useMutation({
    mutationFn: async (args: { name: string; url?: string }) => {
      const r = await callMcp<{ ok?: boolean; error?: string; restarted?: boolean }>(
        scope,
        'toolbox.sysupdate_module',
        args.url
          ? { action: 'install', name: args.name, url: args.url, restart: false }
          : { action: 'enable', name: args.name, restart: false },
      );
      if (r && r.ok === false) throw new Error(r.error ?? 'Aktivace modulu selhala');
      return r;
    },
    onSuccess: () => {
      toast.success(t('addonStore.toastEnableActivate'));
      qc.invalidateQueries({ queryKey: ['addon-all-modules', nk] });
      qc.invalidateQueries({ queryKey: ['addon-installed', nk] });
      qc.invalidateQueries({ queryKey: ['addon-bundles', nk] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const enableManyMut = useMutation({
    mutationFn: async (names: string[]) => {
      const r = await callMcp<{
        ok?: boolean;
        error?: string;
        enabled?: string[];
        skipped?: string[];
      }>(scope, 'toolbox.sysupdate_module', {
        action: 'enable',
        names,
        restart: true,
      });
      if (r && r.ok === false) throw new Error(r.error ?? 'Zapnutí sady selhalo');
      return r;
    },
    onSuccess: (r) => {
      const n = r.enabled?.length ?? 0;
      toast.success(t('addonStore.toastBundleEnabled', { count: n }));
      setPicked([]);
      qc.invalidateQueries({ queryKey: ['addon-all-modules', nk] });
      qc.invalidateQueries({ queryKey: ['addon-installed', nk] });
      qc.invalidateQueries({ queryKey: ['addon-bundles', nk] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const syncMut = useMutation({
    mutationFn: () =>
      callMcp(scope, 'registry.sync', { action: 'pull' }).catch(() => ({ ok: false })),
    onSuccess: () => {
      toast.success(t('addonStore.toastRegistrySync'));
      qc.invalidateQueries({ queryKey: ['addon-registry', nk] });
    },
  });

  const allAvailable = [
    ...disabledLocal.map((m) => ({
      name: m.name,
      version: m.version,
      description: m.description,
      maturity: m.maturity,
      source: 'local (disabled)' as string,
      url: undefined as string | undefined,
      parent: m.parent ?? 'acm',
    })),
    ...remoteOnly.map((m) => ({
      name: m.name,
      version: m.version,
      description: m.description,
      maturity: m.maturity,
      source: m.source,
      url: undefined as string | undefined,
      parent: 'acm' as string,
    })),
  ];

  const packNames = activePack ? new Set(activePack.modules.map((m) => m.name)) : null;
  const filtered = allAvailable.filter((m) => {
    if (packNames && !packNames.has(m.name)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return m.name.includes(q) || (m.description || '').toLowerCase().includes(q);
  });
  const availableTree = nestModules(filtered);

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <div className='flex-1 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
          <Search size={14} className='text-gray-600' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('addonStore.searchModulePlaceholder')}
            className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
          />
        </div>
        <Button
          variant='secondary'
          size='sm'
          onClick={() => syncMut.mutate()}
          loading={syncMut.isPending}
          icon={<GitBranch size={14} />}
        >
          {t('addonStore.syncRegistry')}
        </Button>
        <Button
          variant='secondary'
          size='sm'
          onClick={async () => {
            if (await themedConfirm(t('addonStore.confirmRestartApi'))) {
              restartMut.mutate();
            }
          }}
          loading={restartMut.isPending}
          icon={<RotateCw size={14} />}
          title={t('addonStore.restartApiTitle')}
        >
          {t('addonStore.restartApi')}
        </Button>
        <RefreshButton
          onRefresh={() => {
            refetch();
          }}
          loading={isLoading}
          size='md'
        />
      </div>

      {bundles.length > 0 && (
        <div className='flex flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => setPackFilter(null)}
            className={`rounded-full border px-3 py-1 text-xs ${
              packFilter === null
                ? 'border-blue-500 bg-blue-500/15 text-blue-300'
                : 'border-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            {t('addonStore.packFilterAll', 'Vše')}
          </button>
          {bundles.map((b) => (
            <button
              key={b.id}
              type='button'
              title={b.description}
              onClick={() => setPackFilter(b.id === packFilter ? null : b.id)}
              className={`rounded-full border px-3 py-1 text-xs ${
                packFilter === b.id
                  ? 'border-blue-500 bg-blue-500/15 text-blue-300'
                  : 'border-gray-800 text-gray-400 hover:border-gray-600'
              }`}
            >
              {b.title}
              {b.to_enable.length > 0 ? ` · ${b.to_enable.length}` : ''}
            </button>
          ))}
        </div>
      )}

      <div className='text-xs text-gray-600'>
        {activePack
          ? t('addonStore.packFilterSummary', {
            title: activePack.title,
            pending: activePack.to_enable.length,
            total: activePack.modules.length,
          })
          : t('addonStore.availableSummary', {
            disabledCount: disabledLocal.length,
            remoteCount: remoteOnly.length,
          })}
      </div>

      {activePack && activePack.to_enable.length > 0 && (
        <div className='flex items-center gap-2'>
          <Button
            variant='primary'
            size='sm'
            loading={enableManyMut.isPending}
            onClick={() => enableManyMut.mutate(activePack.to_enable)}
          >
            {t('addonStore.enablePack', { count: activePack.to_enable.length })}
          </Button>
        </div>
      )}

      {picked.length > 0 && (
        <div className='flex items-center gap-2'>
          <Button
            variant='primary'
            size='sm'
            loading={enableManyMut.isPending}
            onClick={() => enableManyMut.mutate(picked)}
          >
            {t('addonStore.enableSelected', { count: picked.length })}
          </Button>
          <Button variant='ghost' size='sm' onClick={() => setPicked([])}>
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {filtered.length === 0
        ? (
          <EmptyState
            icon={<Globe size={24} />}
            title={t('addonStore.allInstalledTitle')}
            description={t('addonStore.allInstalledDesc')}
          />
        )
        : (
          <div className='space-y-2'>
            {availableTree.map((m) => (
              <div key={m.name} className='space-y-1'>
                <AvailableRow m={m} picked={picked} setPicked={setPicked} installMut={installMut} t={t} />
                {m.children.map((c) => (
                  <div key={c.name} className='pl-6'>
                    <AvailableRow m={c} picked={picked} setPicked={setPicked} installMut={installMut} t={t} nested />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── HA / HACS ──────────────────────────────────────────────────────────────

function HaAddonsTab({ scope }: { scope: EffectiveScope }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'ha' | 'hacs'>('ha');

  const [haRepos, setHaRepos] = useState<HaRepo[]>([]);
  const [haLoading, setHaLoading] = useState(false);
  const [haSearch, setHaSearch] = useState('');
  const [haAddRepoUrl, setHaAddRepoUrl] = useState('');
  const [haImporting, setHaImporting] = useState<string | null>(null);

  const [hacsRepos, setHacsRepos] = useState<HacsRepo[]>([]);
  const [hacsLoading, setHacsLoading] = useState(false);
  const [hacsSearch, setHacsSearch] = useState('');
  const [hacsAddRepoUrl, setHacsAddRepoUrl] = useState('');
  const [hacsImporting, setHacsImporting] = useState<string | null>(null);

  useEffect(() => {
    setHaRepos(DEFAULT_HA_REPOS.map((r) => ({ ...r, addons: undefined })));
  }, [scope.node]);

  useEffect(() => {
    setHacsRepos(DEFAULT_HACS_REPOS.map((r) => ({ ...r, addons: undefined })));
  }, [scope.node]);

  async function loadHaRepo(idx: number) {
    const repo = haRepos[idx];
    setHaLoading(true);
    try {
      const r = await callMcp<{
        ok: boolean;
        addons?: Array<{ name: string; slug: string; url?: string }>;
        error?: string;
      }>(scope, 'ha.addon.list', { repo_url: repo.url, repo_name: repo.name }, { silent: true });
      if (!r.ok) throw new Error(r.error ?? 'ha.addon.list selhal');

      const addons = (r.addons ?? []).map((item) => ({
        name: item.name,
        slug: item.slug,
        description: t('addonStore.loadingConfigYaml'),
        version: 'latest',
        url: item.url,
        repository: repo.name,
      }));

      const updated = [...haRepos];
      updated[idx] = { ...repo, addons };
      setHaRepos(updated);
    } catch (e) {
      toast.error(t('addonStore.errLoadRepo', { message: (e as Error).message }));
    } finally {
      setHaLoading(false);
    }
  }

  async function loadHacsRepo(idx: number) {
    const repo = hacsRepos[idx];
    setHacsLoading(true);
    try {
      const r = await callMcp<{
        ok: boolean;
        hacs_manifest?: { name?: string; description?: string; hacs?: string };
        error?: string;
      }>(scope, 'hacs.import', { repo_url: repo.url }, { silent: true });

      if (r.ok && r.hacs_manifest) {
        const data = r.hacs_manifest;
        const addons: HacsAddon[] = [{
          name: data.name || repo.name,
          description: data.description || '',
          version: data.hacs || 'latest',
          url: repo.url,
          repository: repo.name,
          hacsData: data,
        }];
        const updated = [...hacsRepos];
        updated[idx] = { ...repo, addons };
        setHacsRepos(updated);
      } else {
        toast.error(t('addonStore.errLoadHacsJson', { url: repo.url }));
      }
    } catch (e) {
      toast.error(t('addonStore.errLoadRepository', { message: (e as Error).message }));
    } finally {
      setHacsLoading(false);
    }
  }

  async function importHaAddon(addon: HaAddon) {
    setHaImporting(addon.slug);
    try {
      const configUrl = addon.url
        ? addon.url.replace(/\/tree\//, '/raw/').replace(
          /github\.com/,
          'raw.githubusercontent.com',
        ) + '/config.yaml'
        : '';

      await callMcp(scope, 'apphost.addon_import', { url: configUrl, name_override: addon.slug });
      toast.success(t('addonStore.toastAddonImported', { name: addon.name }));
    } catch (e) {
      toast.error(t('addonStore.errImportFailed', { message: (e as Error).message }));
    } finally {
      setHaImporting(null);
    }
  }

  async function importHacsAddon(addon: HacsAddon) {
    setHacsImporting(addon.name);
    try {
      await callMcp(scope, 'apphost.hacs_import', { repo_url: addon.url, name_override: addon.name });
      toast.success(t('addonStore.toastAddonImported', { name: addon.name }));
    } catch (e) {
      toast.error(t('addonStore.errImportFailed', { message: (e as Error).message }));
    } finally {
      setHacsImporting(null);
    }
  }

  function addHaRepo() {
    if (!haAddRepoUrl.trim()) return;
    setHaRepos([...haRepos, {
      name: haAddRepoUrl.split('/').pop()?.replace('.json', '') ?? 'Custom',
      url: haAddRepoUrl,
      addons: undefined,
    }]);
    setHaAddRepoUrl('');
  }

  function addHacsRepo() {
    if (!hacsAddRepoUrl.trim()) return;
    const isGitlab = hacsAddRepoUrl.includes('gitlab.com');
    setHacsRepos([...hacsRepos, {
      name: hacsAddRepoUrl.split('/').pop() ?? 'Custom',
      url: hacsAddRepoUrl,
      type: isGitlab ? 'gitlab' : 'github',
      addons: undefined,
    }]);
    setHacsAddRepoUrl('');
  }

  const haAddons = haRepos.flatMap((r) =>
    (r.addons ?? []).map((a) => ({ ...a, repository: r.name }))
  );
  const hacsAddons = hacsRepos.flatMap((r) =>
    (r.addons ?? []).map((a) => ({ ...a, repository: r.name }))
  );

  const filteredHa = haSearch
    ? haAddons.filter((a) =>
      a.name.toLowerCase().includes(haSearch.toLowerCase()) ||
      a.description.toLowerCase().includes(haSearch.toLowerCase())
    )
    : haAddons;

  const filteredHacs = hacsSearch
    ? hacsAddons.filter((a) =>
      a.name.toLowerCase().includes(hacsSearch.toLowerCase()) ||
      a.description.toLowerCase().includes(hacsSearch.toLowerCase())
    )
    : hacsAddons;

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-2 py-1'>
        <button
          onClick={() => setActiveTab('ha')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'ha'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          {t('addonStore.officialHaAddonsTab')}
        </button>
        <button
          onClick={() => setActiveTab('hacs')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'hacs'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          HACS
        </button>
      </div>

      <div className='bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 space-y-1'>
        <div className='flex items-center gap-2'>
          <span className='text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 leading-none'>
            BETA
          </span>
          <span className='text-xs text-amber-400 font-semibold'>
            {t('addonStore.betaNoticeTitle')}
          </span>
        </div>
        <p className='text-xs text-amber-400/80'>
          {t('addonStore.haImportNoticePre')} <strong>{t('addonStore.haImportNoticeBold')}</strong>
          {' '}
          {t('addonStore.haImportNoticePost')}
        </p>
      </div>

      {activeTab === 'ha' && (
        <>
          <div className='space-y-2'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-widest'>
              {t('addonStore.reposSectionTitle')}
            </span>
            {haRepos.map((r, i) => (
              <div
                key={i}
                className='flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'
              >
                <Home size={14} className='text-cyan-400 flex-shrink-0' />
                <span className='text-sm text-white flex-1'>{r.name}</span>
                <span className='text-[10px] text-gray-600'>
                  {r.addons ? t('addonStore.addonCount', { count: r.addons.length }) : t('addonStore.notLoaded')}
                </span>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => loadHaRepo(i)}
                  loading={haLoading}
                >
                  {r.addons ? t('addonStore.refresh') : t('addonStore.load')}
                </Button>
              </div>
            ))}
            <div className='flex gap-2'>
              <input
                value={haAddRepoUrl}
                onChange={(e) => setHaAddRepoUrl(e.target.value)}
                placeholder={t('addonStore.haRepoUrlPlaceholder')}
                className='flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
              />
              <Button
                variant='secondary'
                size='sm'
                onClick={addHaRepo}
                disabled={!haAddRepoUrl.trim()}
                icon={<Plus size={14} />}
              >
                {t('addonStore.add')}
              </Button>
            </div>
          </div>

          {haAddons.length > 0 && (
            <div className='flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
              <Search size={14} className='text-gray-600' />
              <input
                value={haSearch}
                onChange={(e) => setHaSearch(e.target.value)}
                placeholder={t('addonStore.searchAddonPlaceholder')}
                className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
              />
            </div>
          )}

          {filteredHa.length === 0
            ? (
              <EmptyState
                icon={<Home size={24} />}
                title={t('addonStore.noAddonsTitle')}
                description={t('addonStore.loadRepoAboveDesc')}
              />
            )
            : (
              <div className='space-y-2'>
                {filteredHa.map((a, i) => (
                  <Card key={`${a.slug}-${i}`}>
                    <CardContent className='py-3'>
                      <div className='flex items-center gap-3'>
                        <Home size={16} className='text-cyan-400 flex-shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-semibold text-white'>{a.name}</span>
                            {a.version && (
                              <span className='text-[10px] text-gray-600 font-mono'>
                                v{a.version}
                              </span>
                            )}
                            <Badge variant='secondary'>{a.repository}</Badge>
                          </div>
                          <p className='text-xs text-gray-500 truncate'>{a.description}</p>
                        </div>
                        <div className='flex gap-1 flex-shrink-0'>
                          {a.url && (
                            <a
                              href={a.url}
                              target='_blank'
                              rel='noreferrer'
                              className='p-1.5 text-gray-600 hover:text-white rounded-lg hover:bg-gray-800'
                            >
                              <ExternalLink size={13} />
                            </a>
                          )}
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={() => importHaAddon(a)}
                            loading={haImporting === a.slug}
                            icon={<Download size={12} />}
                          >
                            {t('addonStore.import')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
        </>
      )}

      {activeTab === 'hacs' && (
        <>
          <div className='space-y-2'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-widest'>
              {t('addonStore.hacsSectionTitle')}
            </span>
            {hacsRepos.map((r, i) => (
              <div
                key={i}
                className='flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'
              >
                <GitBranch size={14} className='text-purple-400 flex-shrink-0' />
                <span className='text-sm text-white flex-1'>{r.name}</span>
                <a
                  href={r.url}
                  target='_blank'
                  rel='noreferrer'
                  className='p-1.5 text-gray-600 hover:text-white rounded-lg hover:bg-gray-800'
                >
                  <ExternalLink size={14} />
                </a>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => loadHacsRepo(i)}
                  loading={hacsLoading}
                >
                  {r.addons ? t('addonStore.refresh') : t('addonStore.load')}
                </Button>
              </div>
            ))}
            <div className='flex gap-2'>
              <input
                value={hacsAddRepoUrl}
                onChange={(e) => setHacsAddRepoUrl(e.target.value)}
                placeholder='https://github.com/owner/integration'
                className='flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-purple-500/50'
              />
              <Button
                variant='secondary'
                size='sm'
                onClick={addHacsRepo}
                disabled={!hacsAddRepoUrl.trim()}
                icon={<Plus size={14} />}
              >
                {t('addonStore.add')}
              </Button>
            </div>
          </div>

          {hacsAddons.length > 0 && (
            <div className='flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
              <Search size={14} className='text-gray-600' />
              <input
                value={hacsSearch}
                onChange={(e) => setHacsSearch(e.target.value)}
                placeholder={t('addonStore.searchIntegrationPlaceholder')}
                className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
              />
            </div>
          )}

          {filteredHacs.length === 0
            ? (
              <EmptyState
                icon={<GitBranch size={24} />}
                title={t('addonStore.noHacsIntegrationsTitle')}
                description={t('addonStore.loadRepoAboveDesc')}
              />
            )
            : (
              <div className='space-y-2'>
                {filteredHacs.map((a, i) => (
                  <Card key={`${a.name}-${i}`}>
                    <CardContent className='py-3'>
                      <div className='flex items-center gap-3'>
                        <GitBranch size={16} className='text-purple-400 flex-shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-semibold text-white'>{a.name}</span>
                            <Badge variant='secondary'>{a.repository}</Badge>
                          </div>
                          <p className='text-xs text-gray-500 truncate'>{a.description}</p>
                        </div>
                        <div className='flex gap-1 flex-shrink-0'>
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={() => importHacsAddon(a)}
                            loading={hacsImporting === a.name}
                            icon={<Download size={12} />}
                          >
                            {t('addonStore.import')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
        </>
      )}
    </div>
  );
}

function ChannelBadge({ scope }: { scope: EffectiveScope }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['addon-server-info', nodeKey(scope)],
    queryFn: () => callMcp<{ env: string }>(scope, 'acm-self.ui_context'),
    staleTime: 60_000,
  });
  const env = data?.env ?? 'dev';
  const label = env === 'production'
    ? t('addonStore.channelProduction')
    : env === 'staging'
    ? t('addonStore.channelStaging')
    : t('addonStore.channelDevelopment');
  const cls = env === 'production'
    ? 'bg-green-500/10 border-green-500/20 text-green-400'
    : env === 'staging'
    ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
    : 'bg-blue-500/10 border-blue-500/20 text-blue-400';
  return (
    <span
      className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium ' +
        cls}
    >
      <Shield size={12} />
      {t('addonStore.channelLabel', { env: label })}
    </span>
  );
}

export function InstalledPanel({ scope }: WidgetRenderProps) {
  return <InstalledTab scope={scope ?? { node: null, identity: null }} />;
}

export function AvailablePanel({ scope }: WidgetRenderProps) {
  return <AvailableTab scope={scope ?? { node: null, identity: null }} />;
}

export function HaAddonsPanel({ scope }: WidgetRenderProps) {
  return <HaAddonsTab scope={scope ?? { node: null, identity: null }} />;
}
